import { useEffect, useState } from "react"
import { toast } from "@stardust-ds/react"
import { useForm, FormProvider } from "react-hook-form"
import { yupResolver } from "@hookform/resolvers/yup"
import intl from "react-intl-universal"

import Modal from "../../../../molecules/Modal"
import Input from "../../../../atoms/Input"
import Select from "../../../../atoms/Select"
import Button from "../../../../atoms/Button"
import SwitchComponent from "../../../../atoms/Switch"
import { schema } from "./schemaValidation"
import { useCallback } from "react"
import { fnDecodeStorageToken } from "../../../../../utils/encodeLocalStorage"

import styles from "./index.module.scss"
import { getPostalCodeAddress } from "../../../../../services/plan"
import { usePlanContext } from "../../context"
import { AlertSucces } from "../../../../../assets/icons/AlertSucess"

const A_CUST_CTYPE_OPTIONS = [
  { value: "PF", label: "Pessoa Física" },
  { value: "PJ", label: "Pessoa Jurídica" },
]

export default function ModalFinancialForm({ onClose = () => {}, bIsAnnual }) {
  const [bIsSubmitting, setIsSubmitting] = useState(false)
  const [bPostalCodeValidated, setPostalCodeValidated] = useState(false)
  const [cAuthenticatedLink, setAuthenticatedLink] = useState("")
  const { oCustomer, submitPlan } = usePlanContext()

  const methods = useForm({
    resolver: yupResolver(schema(intl)),
    defaultValues: {},
  })

  const {
    handleSubmit,
    register,
    setValue,
    getValues,
    reset,
    setError,
    trigger,
    formState: { errors },
  } = methods

  useEffect(() => {
    ;[
      "cust_cname",
      "cust_cfantasyname",
      "cust_ccnpj",
      "cust_ccpf",
      "cust_cpostalcode",
      "cust_cstreet",
      "cust_cnumber",
      "cust_cdistrict",
      "cust_ccity",
      "cust_cstate",
      "cust_ccountry",
      "cust_cIE",
      "cust_cIM",
      "cust_cphone",
      "cust_ctype",
      "cust_cemail",
    ].forEach((cKey) => {
      setValue(cKey, oCustomer[cKey] ?? "")
    })
    trigger("cust_ctype")
    setValue("ca_bissimple", oCustomer.cust_etaxframework !== "Não optante")
    setValue("ca_bispublic", oCustomer.cust_etaxframework === "Isento")
  }, [])

  const fnSubmitPlan = useCallback(
    async (oData) => {
      try {
        setIsSubmitting(true)

        oData.sig_bisannual = bIsAnnual
        const cLink = await submitPlan(oData)
        setAuthenticatedLink(cLink)
      } catch (oError) {
        const oData = oError.response?.data ?? {}

        let cMessage = "Erro desconhecido"
        if (oData.errors) {
          cMessage = Object.values(oData.errors).join("\n")
        } else if (oData.message) {
          cMessage = oData.message
        }

        toast({
          type: "error",
          title: "Erro ao atualizar plano",
          description: cMessage,
        })
      }
      setIsSubmitting(false)
    },
    [oCustomer, bIsAnnual],
  )

  const handleClose = useCallback(() => {
    setAuthenticatedLink("")
    onClose()
  }, [onClose])

  const fnIsError = useCallback((cMessage) => (cMessage ? styles.error : ""), [])

  /**
   * @param {string} cValue
   */
  const searchPostalCode = async (cValue) => {
    const cZipCode = cValue.replace(/\D/g, "")
    if (bIsSubmitting) return
    if (cZipCode.length !== 8) {
      setError("cust_cpostalcode", {
        message: intl.get("plan.errors.invalid_postalcode"),
      })
      return
    }

    const oResult = {}
    setIsSubmitting(true)
    try {
      const oResponse = await getPostalCodeAddress(cZipCode)
      Object.entries(oResponse.data.result).forEach(([cKey, cValue]) => {
        switch (cKey) {
          case "logradouro":
            return (oResult.cust_caddress = cValue)
          case "localidade":
            return (oResult.cust_ccity = cValue)
          case "siglaEstado":
            return (oResult.cust_cstate = cValue)
          case "bairro":
            return (oResult.cust_cdistrict = cValue)
        }
      })
      if (Object.keys(oResult).length > 0) {
        oResult.cust_ccountry =
          fnDecodeStorageToken("cLanguage") === "en" ? "Brazil" : "Brasil"
        Object.keys(oResult).forEach((cKey) => setValue(cKey, oResult[cKey]))
      }
      reset("cust_cpostalcode", { keepValues: true })
    } catch (oError) {
      setError("cust_cpostalcode", { message: "CEP inválido" })
    }
    setIsSubmitting(false)
  }

  const cCustType = getValues("cust_ctype")
  return (
    <FormProvider {...methods}>
      <Modal isOpen={true} size="xlg">
        <Modal.Header
          style={{
            padding: "0.5rem",
            justifyContent: "left",
          }}
          onClose={handleClose}
        >
          <span className={styles.titleModal}>
            {intl.get(
              oCustomer.bIsTrial ? "plan.form.title_hire" : "plan.form.title_update",
            )}
          </span>
        </Modal.Header>
        <form
          className={styles.form}
          onSubmit={handleSubmit(fnSubmitPlan, (oData) =>
            console.error("falha ao contratar", oData),
          )}
          autoComplete="off"
        >
          <Modal.Content
            size="lg"
            style={{
              width: "100%",
              display: "flex",
              flexDirection: "column",
              gap: "0.5rem",
            }}
          >
            <div className={styles.formContent}>
              <div className={`${styles.formGroup} ${styles.required}`}>
                <Select
                  label={intl.get("plan.form.cust_ctype")}
                  options={A_CUST_CTYPE_OPTIONS}
                  placeholder={"Selecione..."}
                  onSelect={(oOption) => {
                    setValue("cust_ctype", oOption.value)
                    trigger("cust_ctype")
                  }}
                  value={A_CUST_CTYPE_OPTIONS.find(
                    (oOption) => oOption.value === cCustType,
                  )}
                  searchable={false}
                  clearable={false}
                  width="100%"
                  required
                />
              </div>

              <hr
                className={`${styles.formGroup}`}
                style={{ border: "1px dashed #D2D2D2" }}
              />

              {cCustType === "PF" ? (
                <>
                  <div
                    className={`${styles.formGroupHalf} ${fnIsError(errors.cust_cname)} ${
                      styles.required
                    }`}
                  >
                    <Input
                      label={intl.get("plan.form.cust_cname_pf")}
                      placeholder={intl.get("plan.form.cust_cname_pf")}
                      width="100%"
                      {...register("cust_cname")}
                    />
                    <ErrorSpan oError={errors.cust_cname} />
                  </div>
                  <div
                    className={`${styles.formGroupHalf} ${fnIsError(errors.cust_ccpf)} ${
                      styles.required
                    }`}
                  >
                    <Input
                      label={"CPF"}
                      placeholder={"000.000.000-00"}
                      {...register("cust_ccpf")}
                      onChange={(e) => {
                        const cValue = e.target.value
                          .replace(/\D/g, "")
                          .replace(/(\d{3})(\d)/, "$1.$2")
                          .replace(/(\d{3})(\d)/, "$1.$2")
                          .replace(/(\d{3})(\d{1,2}).*$/, "$1-$2")
                        setValue("cust_ccpf", cValue)
                        if (cValue.length === 14 || errors.cust_ccpf) {
                          trigger("cust_ccpf")
                        }
                      }}
                      onBlur={() => {
                        if (getValues("cust_ccpf").length) {
                          trigger("cust_ccpf")
                        }
                      }}
                      width="100%"
                    />
                    <ErrorSpan oError={errors.cust_ccpf} />
                  </div>
                </>
              ) : (
                <>
                  <div
                    className={`${styles.formGroup} ${fnIsError(errors.cust_cname)} ${
                      styles.required
                    }`}
                  >
                    <Input
                      label={intl.get("plan.form.cust_cname_pj")}
                      placeholder={intl.get("plan.form.cust_cname_pj")}
                      width="100%"
                      {...register("cust_cname")}
                    />
                    <ErrorSpan oError={errors.cust_cname} />
                  </div>
                  <div
                    className={`${styles.formGroupHalf} ${fnIsError(
                      errors.cust_cfantasyname,
                    )} ${styles.required}`}
                  >
                    <Input
                      label={intl.get("plan.form.cust_cfantasyname")}
                      placeholder={intl.get("plan.form.cust_cfantasyname")}
                      width="100%"
                      {...register("cust_cfantasyname")}
                    />
                    <ErrorSpan oError={errors.cust_cfantasyname} />
                  </div>
                  <div
                    className={`${styles.formGroupHalf} ${fnIsError(errors.cust_ccnpj)} ${
                      styles.required
                    }`}
                  >
                    <Input
                      label={"CNPJ"}
                      placeholder={"00.000.000/0000-00"}
                      {...register("cust_ccnpj")}
                      onChange={(e) => {
                        const cValue = e.target.value
                          .replace(/\D/g, "")
                          .replace(/(\d{2})(\d)/, "$1.$2")
                          .replace(/(\d{3})(\d)/, "$1.$2")
                          .replace(/(\d{3})(\d)/, "$1/$2")
                          .replace(/(\d{4})(\d{1,2}).*$/, "$1-$2")
                        setValue("cust_ccnpj", cValue)
                        if (cValue.length === 18 || errors.cust_ccnpj) {
                          trigger("cust_ccnpj")
                        }
                      }}
                      onBlur={() => {
                        if (getValues("cust_ccnpj").length) {
                          trigger("cust_ccnpj")
                        }
                      }}
                      width="100%"
                    />
                    <ErrorSpan oError={errors.cust_ccnpj} />
                  </div>
                  <div
                    className={`${styles.formGroupHalf} ${fnIsError(errors.cust_cIE)} ${
                      styles.required
                    }`}
                  >
                    <Input
                      label={intl.get("plan.form.cust_cIE")}
                      width="100%"
                      {...register("cust_cIE")}
                    />
                    <ErrorSpan oError={errors.cust_cIE} />
                  </div>
                  <div
                    className={`${styles.formGroupHalf} ${fnIsError(errors.cust_cIM)}`}
                  >
                    <Input
                      label={intl.get("plan.form.cust_cIM")}
                      width="100%"
                      {...register("cust_cIM")}
                    />
                    <ErrorSpan oError={errors.cust_cIM} />
                  </div>
                  <div className={`${styles.formGroupHalf} ${styles.switchContainer}`}>
                    <SwitchComponent
                      width="100%"
                      cDisabledText={"Optante pelo simples"}
                      bSingleOption
                      onChange={(bIsChecked) => {
                        setValue("ca_bissimple", bIsChecked)
                        setValue("ca_bispublic", false)
                        trigger("ca_bissimple")
                        trigger("ca_bispublic")
                      }}
                      value={getValues("ca_bissimple")}
                      disabled={getValues("ca_bispublic")}
                    />
                  </div>
                  <div className={`${styles.formGroupHalf} ${styles.switchContainer}`}>
                    <SwitchComponent
                      width="100%"
                      cDisabledText={"Entidade pública"}
                      bSingleOption
                      onChange={(bIsChecked) => {
                        setValue("ca_bispublic", bIsChecked)
                        setValue("ca_bissimple", false)
                        trigger("ca_bispublic")
                        trigger("ca_bissimple")
                      }}
                      value={getValues("ca_bispublic")}
                      disabled={getValues("ca_bissimple")}
                    />
                  </div>
                </>
              )}

              <hr
                className={`${styles.formGroup}`}
                style={{ border: "1px dashed #D2D2D2" }}
              />

              <div
                className={`${styles.formGroupThird} ${fnIsError(
                  errors.cust_cpostalcode,
                )} ${styles.required}`}
              >
                <Input
                  label={"CEP"}
                  placeholder={"00000-000"}
                  {...register("cust_cpostalcode")}
                  onChange={async (e) => {
                    const cValue = e.target.value
                      .replace(/\D/g, "")
                      .replace(/(\d{5})(\d{1,3}).*$/, "$1-$2")
                    setValue("cust_cpostalcode", cValue)
                    setPostalCodeValidated(false)
                  }}
                  onBlur={async () => {
                    const cValue = getValues("cust_cpostalcode")
                    if (!bPostalCodeValidated && cValue.length === 9) {
                      await searchPostalCode(cValue)
                      setPostalCodeValidated(true)
                    }
                  }}
                  width="100%"
                />
                <ErrorSpan oError={errors.cust_cpostalcode} />
              </div>
              <div
                className={`${styles.formGroupThird} ${fnIsError(errors.cust_ccity)} ${
                  styles.required
                }`}
              >
                <Input
                  label={"Cidade"}
                  placeholder={"Cidade"}
                  width="100%"
                  {...register("cust_ccity")}
                  disabled
                />
                <ErrorSpan oError={errors.cust_ccity} />
              </div>
              <div
                className={`${styles.formGroupThird} ${fnIsError(errors.cust_cstate)} ${
                  styles.required
                }`}
              >
                <Input
                  label={"Estado"}
                  placeholder={"UF"}
                  width="100%"
                  {...register("cust_cstate")}
                  disabled
                />
                <ErrorSpan oError={errors.cust_cstate} />
              </div>

              <div
                className={`${styles.formGroupTwoThird} ${fnIsError(
                  errors.cust_cstreet,
                )} ${styles.required}`}
              >
                <Input
                  label={"Endereço"}
                  placeholder={"Endereço"}
                  width="100%"
                  {...register("cust_cstreet")}
                  disabled
                />
                <ErrorSpan oError={errors.cust_cstreet} />
              </div>
              <div
                className={`${styles.formGroupThird} ${fnIsError(
                  errors.cust_cdistrict,
                )} ${styles.required}`}
              >
                <Input
                  label={"Bairro"}
                  placeholder={"Bairro"}
                  width="100%"
                  {...register("cust_cdistrict")}
                  disabled
                />
                <ErrorSpan oError={errors.cust_cdistrict} />
              </div>

              <div
                className={`${styles.formGroupThird} ${fnIsError(errors.cust_cnumber)} ${
                  styles.required
                }`}
              >
                <Input
                  label={"Número"}
                  placeholder={"Número"}
                  width="100%"
                  {...register("cust_cnumber")}
                />
                <ErrorSpan oError={errors.cust_cnumber} />
              </div>
              <div
                className={`${styles.formGroupTwoThird} ${fnIsError(
                  errors.cust_ccomplement,
                )}`}
              >
                <Input
                  label={"Complemento"}
                  width="100%"
                  {...register("cust_ccomplement")}
                />
                <ErrorSpan oError={errors.cust_ccomplement} />
              </div>

              <hr
                className={`${styles.formGroup}`}
                style={{ border: "1px dashed #D2D2D2" }}
              />

              <div
                className={`${styles.formGroupTwoThird} ${fnIsError(
                  errors.cust_cemail,
                )} ${styles.required}`}
              >
                <Input
                  label={"E-mail"}
                  placeholder={"exemplo@dominio.com"}
                  type="email"
                  width="100%"
                  {...register("cust_cemail")}
                />
                <ErrorSpan oError={errors.cust_cemail} />
              </div>

              <div
                className={`${styles.formGroupThird} ${fnIsError(errors.cust_cphone)} ${
                  styles.required
                }`}
              >
                <Input
                  label={"Telefone"}
                  placeholder={"(00) 0000-0000"}
                  width="100%"
                  {...register("cust_cphone")}
                  // prettier-ignore
                  onChange={(e) => {
                    const cValue = e.target.value
                      .replace(/\D/g, "")
                      .replace(/^(\d)(\d*)/, "($1$2")
                      .replace(/^\((\d{2})(\d+)$/, "($1) $2")
                      .replace(/(\d{4})(\d+)$/, "$1-$2")
                      .replace(/(\d{4})-(\d)(\d{4})\d*$/, "$1$2-$3")
                    setValue("cust_cphone", cValue)
                    if (errors.cust_cphone) {
                      trigger("cust_cphone")
                    }
                  }}
                />
                <ErrorSpan oError={errors.cust_cphone} />
              </div>
            </div>

            <div className={styles.containerButton}>
              <Button type="button" variant="outline" onClick={handleClose}>
                {intl.get("back")}
              </Button>
              <Button type="submit" isLoading={bIsSubmitting} disabled={bIsSubmitting}>
                {intl.get(
                  oCustomer.bIsTrial ? "plan.form.btn_hire" : "plan.form.btn_update",
                )}
              </Button>
            </div>
          </Modal.Content>
        </form>
      </Modal>
      {cAuthenticatedLink && (
        <ModalConfirmation
          onConfirm={() => {
            window.open(cAuthenticatedLink, "_blank", "noopener,noreferrer")
            handleClose()
          }}
        />
      )}
    </FormProvider>
  )
}

const ModalConfirmation = ({ onConfirm = () => {} }) => {
  return (
    <Modal isOpen={true} size="xlg">
      <Modal.Header
        style={{
          padding: "0.5rem",
          justifyContent: "left",
        }}
        bIconClose={false}
      >
        <span className={styles.titleModal}>Pedido cadastrado</span>
      </Modal.Header>
      <Modal.Content
        size="lg"
        style={{
          width: "100%",
          display: "flex",
          flexDirection: "column",
          height: "100%",
          gap: "0.5rem",
        }}
      >
        <div
          className={styles.modalConfirm}
          style={{ height: "100%", justifyContent: "center" }}
        >
          <div style={{ display: "flex", justifyContent: "center" }}>
            <AlertSucces width="100px" />
          </div>
          <p>{intl.get("plan.hire_success")}</p>

          <div className={styles.btnContainer}>
            <Button type="button" onClick={onConfirm}>
              {intl.get("plan.form.btn_continue")}
            </Button>
          </div>
        </div>
      </Modal.Content>
    </Modal>
  )
}

const ErrorSpan = ({ oError }) => {
  if (!oError) return null
  return <span>{oError.message}</span>
}
